import React, { useRef } from "react";
import { GenerationSection } from "./GenerateDraftSidebar";
import {
  ModelInfo,
  DocumentType,
  DocumentAnalysisResponse,
  SectionGenerationState,
  InputPanelState,
  UploadedFileInfo,
} from "../types";
import GenerateDraftInputPanel, { GenerateDraftInputPanelHandle } from "./GenerateDraftInputPanel";
import TreatmentPlanSection from "./TreatmentPlanSection";
import "./SectionGenerationStep.css";

interface SectionGenerationStepProps {
  models: ModelInfo[];
  activeSection: GenerationSection;
  sectionStates: Map<GenerationSection, SectionGenerationState>;
  onSectionStateChange: (section: GenerationSection, updates: Partial<SectionGenerationState>) => void;
  onGenerate: (data: {
    section: GenerationSection;
    modelId: string;
    systemPrompt: string;
    userPrompt: string;
    referenceData: Array<{ title: string; content: any }>;
    ragEnabled: boolean;
    ragNumChunks: number;
    ragMinScore: number;
  }) => void;
  onSectionDataChange: (section: GenerationSection, newData: any) => void;
  analysisResults: Record<DocumentType, DocumentAnalysisResponse | null>;
  generatedSections: Map<GenerationSection, any>;
  inputStateMap: Record<DocumentType, InputPanelState>;
  uploadedFiles: Record<string, UploadedFileInfo>;
  sessionId: number | null;
  isGenerating: boolean;
}

const createInitialSectionState = (): SectionGenerationState => ({
  selectedModel: "",
  systemPrompt: "",
  userPrompt: "",
  selectedReferences: [],
  generatedContent: null,
  isGenerating: false,
  lastGeneratedAt: null,
  lastGeneratedModel: null,
  hasUnsavedChanges: false,
  ragEnabled: true,
  ragNumChunks: 5,
  ragMinScore: 0.3,
});

const SectionGenerationStep: React.FC<SectionGenerationStepProps> = ({
  models,
  activeSection,
  sectionStates,
  onSectionStateChange,
  onGenerate,
  onSectionDataChange,
  analysisResults,
  generatedSections,
  inputStateMap,
  uploadedFiles,
  sessionId,
  isGenerating,
}) => {
  const inputPanelRef = useRef<GenerateDraftInputPanelHandle>(null);

  const getSectionState = (section: GenerationSection): SectionGenerationState => {
    return sectionStates.get(section) || createInitialSectionState();
  };

  const getSectionName = (section: GenerationSection): string => {
    return section
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const currentState = getSectionState(activeSection);
  const showAdvancedOptions = currentState.showAdvancedOptions || false;
  const previewState = currentState.previewState || { isActive: false };

  return (
    <div className="section-generation-step">
      <div className="section-generation-content">
        <div className="section-content-left">
          <div className="section-panel-header">
            <h3>{getSectionName(activeSection)}</h3>
            <button
              className="btn btn-secondary"
              onClick={() =>
                onSectionStateChange(activeSection, {
                  showAdvancedOptions: !showAdvancedOptions,
                  hasUnsavedChanges: true,
                })
              }
              disabled={currentState.isGenerating || previewState.isActive}
            >
              {showAdvancedOptions ? "Back" : "Settings"}
            </button>
          </div>
          <GenerateDraftInputPanel
            ref={inputPanelRef}
            activeSection={activeSection}
            models={models}
            onGenerate={onGenerate}
            sectionState={currentState}
            onSectionStateChange={(updates) => onSectionStateChange(activeSection, updates)}
            analysisResults={analysisResults}
            generatedSections={generatedSections}
            inputStateMap={inputStateMap}
            onSectionDataChange={onSectionDataChange}
            uploadedFiles={uploadedFiles}
            sessionId={sessionId}
          />
        </div>

        <div className="section-content-right">
          <div className="section-results">
            <div className="section-results-header">
              <h3>Generated Content</h3>
              <div className="section-results-actions">
                {currentState.generatedContent &&
                  currentState.ragChunks &&
                  currentState.ragChunks.length > 0 && (
                    <button
                      className="btn btn-secondary"
                      onClick={() =>
                        onSectionStateChange(activeSection, {
                          showRagChunks: !currentState.showRagChunks,
                        })
                      }
                    >
                      {currentState.showRagChunks ? "Hide Sources" : "View Sources"}
                    </button>
                  )}
                <button
                  className="btn btn-primary btn-generate"
                  onClick={() => inputPanelRef.current?.handleGenerate()}
                  disabled={
                    !currentState.selectedModel ||
                    currentState.isGenerating ||
                    previewState.isActive
                  }
                >
                  {currentState.isGenerating ? "Generating..." : "Generate"}
                </button>
              </div>
            </div>

            {currentState.generatedContent ? (
              <div className="section-results-content">
                <TreatmentPlanSection
                  section={activeSection}
                  data={currentState.generatedContent}
                  editable={true}
                  onDataChange={(newData) => onSectionDataChange(activeSection, newData)}
                />

                {currentState.showRagChunks &&
                  currentState.ragChunks &&
                  currentState.ragChunks.length > 0 && (
                    <div className="rag-chunks-section">
                      <h4 className="rag-chunks-title">Retrieved Sources</h4>
                      <div className="rag-chunks-list">
                        {currentState.ragChunks.map((chunk, index) => (
                          <details key={chunk.chunk_id || `chunk-${index}`} className="rag-chunk-details">
                            <summary className="rag-chunk-summary">
                              <span className="chunk-number">#{index + 1}</span>
                              <span className="chunk-source">{chunk.source}</span>
                              {chunk.page_number && <span className="chunk-page">Page {chunk.page_number}</span>}
                              <span className="chunk-score">{(chunk.score * 100).toFixed(0)}%</span>
                            </summary>
                            <div className="rag-chunk-content">{chunk.content}</div>
                          </details>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            ) : (
              <div className="section-results-empty">
                <span className="empty-icon">📝</span>
                <h4>No Content Generated</h4>
                <p>Select reference documents and click "Generate Section" to create content</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionGenerationStep;
